/**
 * pickFile
 *
 * Shows an action sheet with three sources:
 *   • Take Photo    — camera (expo-image-picker)
 *   • Choose from Library — photo library (expo-image-picker)
 *   • Browse Files  — system file picker (expo-document-picker)
 *
 * Normalises every source into the same asset shape so call-sites
 * don't need to branch on how the file was selected.
 */

import { Alert, Platform } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system/legacy';

export type PickedFile = {
  uri: string;
  name: string;
  mimeType: string;
  size: number;
};

async function imageResultToFile(
  asset: ImagePicker.ImagePickerAsset,
): Promise<PickedFile> {
  const mimeType = asset.mimeType || 'image/jpeg';
  const ext = mimeType === 'image/png' ? 'png' : 'jpg';
  const name = `photo_${Date.now()}.${ext}`;
  let size = 0;
  try {
    const info = await FileSystem.getInfoAsync(asset.uri);
    if (info.exists && 'size' in info) size = (info as any).size ?? 0;
  } catch {
    // size stays 0 — not critical
  }
  return { uri: asset.uri, name, mimeType, size };
}

/**
 * Opens the system document picker directly (no action sheet), resolving to
 * normalised PickedFile assets.  Use this for "Google Drive" and "Dropbox"
 * buttons — the Files app on iOS surfaces those cloud locations natively.
 */
export async function pickFileDirect(allowMultiple = false): Promise<PickedFile[] | null> {
  try {
    const result = await DocumentPicker.getDocumentAsync({
      type: '*/*',
      copyToCacheDirectory: true,
      multiple: allowMultiple,
    });
    if (result.canceled || !result.assets?.length) return null;
    return result.assets.map((a) => {
      const ext = a.name?.split('.').pop() || '';
      return {
        uri: a.uri,
        name: a.name || `file_${Date.now()}.${ext}`,
        mimeType: a.mimeType || `application/${ext}`,
        size: a.size || 0,
      };
    });
  } catch {
    return null;
  }
}

/**
 * @param allowMultiple  Whether to support multi-file selection (only applies
 *                       to "Browse Files"; camera/library always return one).
 *
 * Web/Electron: skip the camera/library/files action sheet entirely and go
 * straight to the system file picker (DocumentPicker on web is just an
 * `<input type="file">`, which already exposes camera + library + cloud
 * sources via the OS). The wrapper Alert.alert is also a multi-button
 * action sheet, and inside another Modal (e.g. the New Message compose
 * sheet) the polyfill stacks behind it on some browsers — so the click
 * appears to do nothing. Bypassing it is both simpler UX and bulletproof.
 */
export async function pickFile(allowMultiple = false): Promise<PickedFile[] | null> {
  if (Platform.OS === 'web') {
    return pickFileDirect(allowMultiple);
  }

  return new Promise((resolve) => {
    Alert.alert(
      'Add File',
      undefined,
      [
        {
          text: 'Take Photo',
          onPress: async () => {
            try {
              const perm = await ImagePicker.requestCameraPermissionsAsync();
              if (!perm.granted) {
                Alert.alert(
                  'Camera access needed',
                  'Please allow camera access in Settings to take a photo.',
                );
                resolve(null);
                return;
              }
              const result = await ImagePicker.launchCameraAsync({
                mediaTypes: ['images'],
                allowsEditing: false,
                quality: 0.9,
                base64: false,
              });
              if (result.canceled || !result.assets?.length) {
                resolve(null);
                return;
              }
              const file = await imageResultToFile(result.assets[0]);
              resolve([file]);
            } catch {
              resolve(null);
            }
          },
        },
        {
          text: 'Choose from Library',
          onPress: async () => {
            try {
              const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
              if (!perm.granted) {
                Alert.alert(
                  'Photo library access needed',
                  'Please allow photo library access in Settings.',
                );
                resolve(null);
                return;
              }
              const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ['images'],
                allowsEditing: false,
                quality: 0.9,
                base64: false,
                allowsMultipleSelection: allowMultiple,
              });
              if (result.canceled || !result.assets?.length) {
                resolve(null);
                return;
              }
              const files = await Promise.all(result.assets.map(imageResultToFile));
              resolve(files);
            } catch {
              resolve(null);
            }
          },
        },
        {
          text: 'Browse Files',
          onPress: async () => {
            try {
              const result = await DocumentPicker.getDocumentAsync({
                type: '*/*',
                copyToCacheDirectory: true,
                multiple: allowMultiple,
              });
              if (result.canceled || !result.assets?.length) {
                resolve(null);
                return;
              }
              const files: PickedFile[] = result.assets.map((a) => {
                const ext = a.name?.split('.').pop() || '';
                return {
                  uri: a.uri,
                  name: a.name || `file_${Date.now()}.${ext}`,
                  mimeType: a.mimeType || `application/${ext}`,
                  size: a.size || 0,
                };
              });
              resolve(files);
            } catch {
              resolve(null);
            }
          },
        },
        {
          text: 'Cancel',
          style: 'cancel',
          onPress: () => resolve(null),
        },
      ],
      { cancelable: true, onDismiss: () => resolve(null) },
    );
  });
}
